import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { Button, Dialog, TextField, Card, CardContent, Link, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';

import { RootState, AppDispatch } from '../redux/store';
import { fetchUsers, addUser, updateUser } from '../users/redux/usersSlice';
import { DataTable } from '../shared/components/DataTable';
import { BFF_BASE_URL } from '../auth/authApi';
import { LoadingButton } from '../shared/components/LoadingButton';
import { Section } from '../shared/components/Section';
import { GlowAlert } from '../shared/components/GlowAlert';


interface NewUser {
  name: string;
  email: string;
  password?: string;
}

const UsersPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const users = useSelector((state: RootState) => state.users.users);
  const loading = useSelector((state: RootState) => state.users.loading);
  const error = useSelector((state: RootState) => state.users.error);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState<NewUser>({ name: '', email: '', password: '' });
  const [creatingUser, setCreatingUser] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [editFormData, setEditFormData] = useState<NewUser>({ name: '', email: '' });
  const [editingUserId, setEditingUserId] = useState(false);

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  const handleAddClick = () => {
    setFormData({ name: '', email: '', password: '' });
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.email || !formData.password) {
      alert('Please fill in all fields');
      return;
    }

    setCreatingUser(true);
    try {
      const response = await axios.post(`${BFF_BASE_URL}/admin/users`, {
        display_name: formData.name,
        email: formData.email,
        password: formData.password,
      });

      dispatch(addUser({
        id: response.data.id,
        display_name: formData.name,
        email: formData.email,
        phone_number: undefined,
        is_admin: false,
        is_active: true,
        conversation_count: 0,
        message_count: 0,
        created_at: new Date().toISOString(),
        last_seen_at: undefined,
      }));

      setOpenDialog(false);
      setFormData({ name: '', email: '', password: '' });
    } catch (err: any) {
      console.error('Failed to create user:', err);
      alert(err?.response?.data?.error || 'Failed to create user');
    } finally {
      setCreatingUser(false);
    }
  };


  const handleEdit = (user: any) => {
    setEditingUser(user);
    setEditFormData({ name: user.display_name || '', email: user.email || '' });
  };

  const handleEditSave = async () => {
    if (!editFormData.name || !editFormData.email) {
      alert('Please fill in all fields');
      return;
    }

    setEditingUserId(true);
    try {
      await axios.put(`${BFF_BASE_URL}/admin/users/${editingUser.id}`, {
        display_name: editFormData.name,
        email: editFormData.email,
      });

      dispatch(updateUser({
        ...editingUser,
        display_name: editFormData.name,
        email: editFormData.email,
      }));

      setEditingUser(null);
      setEditFormData({ name: '', email: '' });
    } catch (err) {
      console.error('Failed to update user:', err);
      alert('Failed to update user');
    } finally {
      setEditingUserId(false);
    }
  };

  const handleDelete = (id: string) => {
    // TODO: Add API endpoint to delete user
  };

  const columns = [
    { key: 'display_name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'phone_number', label: 'Phone' },
    { key: 'is_admin', label: 'Admin', render: (val: unknown) => val ? 'Yes' : 'No' },
    { key: 'is_active', label: 'Active', render: (val: unknown) => val ? '✓' : '✗' },
    { key: 'conversation_count', label: 'Conversations' },
    {
      key: 'message_count',
      label: 'Messages',
      render: (val: unknown, row: any) => (
        <Link component={RouterLink} to={`/messages/${row.id}`}>
          {String(val ?? 0)}
        </Link>
      ),
    },
    { key: 'created_at', label: 'Joined', render: (val: unknown) => new Date(val as string).toLocaleDateString() },
    { key: 'last_seen_at', label: 'Last Seen', render: (val: unknown) => val ? new Date(val as string).toLocaleDateString() : 'Never' },
  ];

  return (
    <Section title="Users" onRefresh={() => dispatch(fetchUsers())} refreshing={loading}>
      <Card sx={{ mb: 2.5, backgroundColor: 'background.paper' }}>
        <CardContent>
          <Typography variant="body2" color="textPrimary" sx={{ lineHeight: 1.6 }}>
            <strong>What is this?</strong> This is the list of all registered users on the VictorIA system,
            showing their account status, activity, and conversation history.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.6, mt: 1 }}>
            <strong>Purpose:</strong> Manage user accounts, monitor engagement, and track when users last
            interacted with the system.
          </Typography>
        </CardContent>
      </Card>

      {error && <GlowAlert sx={{ mb: 2.5 }}>{error}</GlowAlert>}
      {loading && <GlowAlert severity="info">Loading users...</GlowAlert>}

      {!loading && (
        <>
          <div style={{ marginBottom: '20px' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddClick}
            >
              Add User
            </Button>
          </div>

          <DataTable
            columns={columns}
            data={users}
            getRowId={(row) => row.id}
            onEdit={handleEdit}
            onDelete={(row) => handleDelete(row.id)}
          />

          <Dialog open={openDialog} onClose={() => !creatingUser && setOpenDialog(false)} maxWidth="sm" fullWidth>
            <div style={{ padding: '20px' }}>
              <h2>Create New User</h2>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                margin="normal"
                disabled={creatingUser}
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                margin="normal"
                disabled={creatingUser}
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                margin="normal"
                disabled={creatingUser}
              />
              <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                <LoadingButton
                  variant="contained"
                  onClick={handleSave}
                  loading={creatingUser}
                >
                  {creatingUser ? 'Creating...' : 'Create'}
                </LoadingButton>
                <Button
                  variant="outlined"
                  onClick={() => setOpenDialog(false)}
                  disabled={creatingUser}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Dialog>

          <Dialog open={Boolean(editingUser)} onClose={() => !editingUserId && setEditingUser(null)} maxWidth="sm" fullWidth>
            <div style={{ padding: '20px' }}>
              <h2>Edit User</h2>
              <TextField
                fullWidth
                label="Name"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                margin="normal"
                disabled={editingUserId}
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                margin="normal"
                disabled={editingUserId}
              />
              <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                <LoadingButton
                  variant="contained"
                  onClick={handleEditSave}
                  loading={editingUserId}
                >
                  {editingUserId ? 'Saving...' : 'Save'}
                </LoadingButton>
                <Button
                  variant="outlined"
                  onClick={() => setEditingUser(null)}
                  disabled={editingUserId}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Dialog>
        </>
      )}
    </Section>
  );
};

export default UsersPage;
